import { logger } from "./logger";

// Use the built-in PromiseRejectionEvent type and omit the conflicting methods
type PromiseRejectionEventSafe = Omit<PromiseRejectionEvent, 'preventDefault'> & {
  preventDefault?: () => void;
};

// Global error handler setup
export function setupGlobalErrorHandling() {
  // Capture unhandled JavaScript errors
  window.addEventListener("error", (event: ErrorEvent) => {
    logger.error("🚨 Unhandled JavaScript error", {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    });
  });

  // Capture unhandled promise rejections
  window.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEventSafe) => {
      logger.error("🚨 Unhandled promise rejection", {
        reason: event.reason,
        reasonType: typeof event.reason,
        reasonMessage: event.reason?.message,
        reasonStack: event.reason?.stack,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      });

      // Prevent the default console error
      event.preventDefault?.();
    },
  );

  // Network connectivity monitoring
  window.addEventListener("online", () => {
    logger.info("🌐 Network connection restored");
  });

  window.addEventListener("offline", () => {
    logger.warn("📵 Network connection lost");
  });

  // Page visibility changes (useful for debugging)
  document.addEventListener("visibilitychange", () => {
    logger.debug(`👁️ Page visibility changed to: ${document.visibilityState}`);
  });

  // Note: Console error monitoring is handled by errorInterceptor.ts to avoid duplication

  // Log initial setup
  logger.info("🛡️ Global error handling initialized", {
    userAgent: navigator.userAgent,
    online: navigator.onLine,
    cookieEnabled: navigator.cookieEnabled,
    language: navigator.language,
    platform: navigator.platform,
    screen: {
      width: screen.width,
      height: screen.height,
      colorDepth: screen.colorDepth,
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    timestamp: new Date().toISOString(),
  });
}

// Performance monitoring
export function logPerformanceMetrics() {
  if (typeof window !== "undefined" && "performance" in window) {
    window.addEventListener("load", () => {
      setTimeout(() => {
        const navigation = performance.getEntriesByType(
          "navigation",
        )[0] as PerformanceNavigationTiming;
        const paint = performance.getEntriesByType("paint");

        logger.info("📊 Performance metrics", {
          // Navigation timing
          domContentLoaded: Math.round(
            navigation.domContentLoadedEventEnd - navigation.fetchStart,
          ),
          loadComplete: Math.round(
            navigation.loadEventEnd - navigation.fetchStart,
          ),
          firstByte: Math.round(
            navigation.responseStart - navigation.fetchStart,
          ),
          domInteractive: Math.round(
            navigation.domInteractive - navigation.fetchStart,
          ),

          // Paint timing
          firstPaint:
            paint.find((p) => p.name === "first-paint")?.startTime || 0,
          firstContentfulPaint:
            paint.find((p) => p.name === "first-contentful-paint")?.startTime ||
            0,

          // Connection info
          connectionType:
            (navigator as any).connection?.effectiveType || "unknown",
          downlink: (navigator as any).connection?.downlink || "unknown",

          timestamp: new Date().toISOString(),
        });
      }, 0);
    });
  }
}

// Resource loading error monitoring
export function monitorResourceErrors() {
  // Monitor for failed resource loads (images, scripts, etc.)
  window.addEventListener(
    "error",
    (event) => {
      if (event.target !== window && event.target instanceof Element) {
        const element = event.target as Element;
        logger.error("📦 Resource loading failed", {
          type: element.tagName?.toLowerCase(),
          src: (element as any).src || (element as any).href,
          id: element.id,
          className: element.className,
          outerHTML: element.outerHTML?.substring(0, 200),
          timestamp: new Date().toISOString(),
        });
      }
    },
    true,
  ); // Use capture phase to catch resource errors
}
